/**
 * TalentBannerEditorPanel — Iter-290b Sprint B
 *
 * Self-contained editor that lets a logged-in talent pick their banner kind
 * (gradient / color / image), preview it live, and save. Used inside the
 * Talent → Profile Info tab grid.
 *
 * The component manages its own load / save lifecycle and uses the existing
 * api helper so it inherits auth headers.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Upload, RotateCcw, Loader2, Image as ImageIcon, Palette, Layers } from 'lucide-react';
import api from '../utils/api';
import { downscaleImage } from '../utils/imageDownscale';
import TalentBanner, { GRADIENT_PRESETS } from './TalentBanner';

const DEFAULT_KIND = 'gradient';
const DEFAULT_VALUE = 'cyan-violet';

export default function TalentBannerEditorPanel() {
  const [kind, setKind] = useState(DEFAULT_KIND);
  const [value, setValue] = useState(DEFAULT_VALUE);
  const [offsetY, setOffsetY] = useState(50);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const fileInputRef = useRef(null);

  // Hydrate from current profile
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await api.get('/talent/profile');
        const p = res.data?.profile || res.data || {};
        if (!cancel) {
          setKind(p.banner_kind || DEFAULT_KIND);
          setValue(p.banner_value || (p.banner_kind ? '' : DEFAULT_VALUE));
          setOffsetY(p.banner_image_offset_y ?? 50);
        }
      } catch {
        // non-fatal — fall back to defaults
      } finally {
        if (!cancel) setLoaded(true);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const handleSave = async () => {
    if (!value) {
      toast.error('Pick a banner first');
      return;
    }
    setSaving(true);
    try {
      await api.patch('/talent/banner', { kind, value, offset_y: offsetY });
      toast.success('Banner saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      await api.delete('/talent/banner');
      setKind(DEFAULT_KIND);
      setValue(DEFAULT_VALUE);
      setOffsetY(50);
      toast.success('Banner reset to default');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally {
      setSaving(false);
    }
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', await downscaleImage(f, { maxEdge: 2400 }));
      const res = await api.post('/talent/banner/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setKind('image');
      setValue(res.data.banner_value);
      toast.success('Image uploaded — preview below');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (!loaded) {
    return (
      <div className="flex items-center justify-center py-10 text-slate-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading banner…
      </div>
    );
  }

  return (
    <Card data-testid="banner-editor-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Profile Banner</CardTitle>
        <CardDescription>Personalize your public profile with a custom cover image, color, or gradient.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Live preview */}
        <div className="rounded-xl overflow-hidden border border-slate-200">
          <TalentBanner kind={kind} value={value} offsetY={offsetY} className="h-32 sm:h-40" testId="banner-editor-preview" />
        </div>

        {/* Kind switcher */}
        <div className="flex flex-wrap gap-2" data-testid="banner-kind-switcher">
          {[
            { id: 'gradient', label: 'Gradient', Icon: Layers },
            { id: 'color', label: 'Solid color', Icon: Palette },
            { id: 'image', label: 'Image', Icon: ImageIcon },
          ].map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => {
                setKind(id);
                if (id === 'gradient' && !GRADIENT_PRESETS[value]) setValue(DEFAULT_VALUE);
                if (id === 'color' && !/^#[0-9a-f]{3,6}$/i.test(value)) setValue('#0ea5e9');
                if (id === 'image' && !(value || '').startsWith('/api/')) setValue('');
              }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 border ${kind === id ? 'bg-cyan-50 border-cyan-300 text-cyan-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
              data-testid={`banner-kind-${id}`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {/* Per-kind editor */}
        {kind === 'gradient' && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="banner-gradient-grid">
            {Object.entries(GRADIENT_PRESETS).map(([key, preset]) => (
              <button
                key={key}
                type="button"
                onClick={() => setValue(key)}
                className={`h-12 rounded-lg overflow-hidden border-2 ${value === key ? 'border-cyan-500 ring-2 ring-cyan-200' : 'border-slate-200 hover:border-slate-300'}`}
                style={{ background: preset.swatch }}
                title={preset.label}
                data-testid={`banner-gradient-${key}`}
                aria-label={preset.label}
              >
                <span className="sr-only">{preset.label}</span>
              </button>
            ))}
          </div>
        )}

        {kind === 'color' && (
          <div className="flex items-center gap-3" data-testid="banner-color-row">
            <input
              type="color"
              value={/^#[0-9a-f]{6}$/i.test(value) ? value : '#0ea5e9'}
              onChange={(e) => setValue(e.target.value)}
              className="w-14 h-12 rounded-lg cursor-pointer border border-slate-200"
              data-testid="banner-color-picker"
            />
            <Input
              value={value || ''}
              onChange={(e) => setValue(e.target.value)}
              placeholder="#0ea5e9"
              className="font-mono uppercase max-w-xs"
              data-testid="banner-color-hex"
            />
          </div>
        )}

        {kind === 'image' && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFile}
                className="hidden"
                data-testid="banner-file-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="banner-upload-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Upload image
              </Button>
              <span className="text-xs text-slate-500">Or paste a public image URL below</span>
            </div>
            <Input
              value={value || ''}
              onChange={(e) => setValue(e.target.value)}
              placeholder="/api/talent/banner/file/… or https://…"
              className="text-xs"
              data-testid="banner-image-url"
            />
            <div data-testid="banner-offset-row">
              <Label className="text-xs">Vertical position ({offsetY}%)</Label>
              <input
                type="range"
                min="0"
                max="100"
                value={offsetY}
                onChange={(e) => setOffsetY(Number(e.target.value))}
                className="w-full"
                data-testid="banner-offset-slider"
              />
            </div>
          </div>
        )}

        {/* Save / reset */}
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
          <Button onClick={handleSave} disabled={saving} data-testid="banner-save-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save banner
          </Button>
          <Button variant="outline" onClick={handleReset} disabled={saving} data-testid="banner-reset-btn">
            <RotateCcw className="w-4 h-4 mr-2" /> Reset to default
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
